import { Navigate, createBrowserRouter } from "react-router-dom";
import "./index.css";
import { lazy } from "react";
import { createRealmsbaseRoutes } from "realmsbase-dashboard";
import { getEnvironment } from "realms-react-utils";
import { AssetGuard } from "@page-guards/AssetGuard";
import { AdminGuard } from "@page-guards/AdminGuard";
import { NinePatchImageEditor } from "mmo-ui";
import { Box } from "@chakra-ui/react";
import { Environment } from "realms-utils";
import { LoadingTaskWrapper } from "@utils/loading/LoadingTaskWrapper";
import { GameLoadingTaskId } from "@utils/loading/models";

console.log(`Arcadian Realms ${getEnvironment()} mode`);
const IS_DEV = getEnvironment() === Environment.Development;

// Code splitting pages

const CharacterCreationPage = lazy(() =>
  import("./pages/character-creation-page/CharacterCreationPage").then(
    ({ CharacterCreationPage }) => ({ default: CharacterCreationPage })
  )
);

const CharacterSelectionPage = lazy(() =>
  import("./pages/character-selection-page/CharacterSelectionPage").then(
    ({ CharacterSelectionPage }) => ({ default: CharacterSelectionPage })
  )
);

const GamePage = lazy(() =>
  import("./pages/game-page/GamePage").then(({ GamePage }) => ({
    default: GamePage,
  }))
);

const LoginPage = lazy(() =>
  import("./pages/LoginPage").then(({ LoginPage }) => ({ default: LoginPage }))
);

const RegisterPage = lazy(() =>
  import("./pages/RegisterPage").then(({ RegisterPage }) => ({
    default: RegisterPage,
  }))
);

const SetTokenPage = lazy(() =>
  import("./pages/internal/SetTokenPage").then(({ SetTokenPage }) => ({
    default: SetTokenPage,
  }))
);

const AdminDashboardPage = lazy(() =>
  import("./pages/admin/admin-dashboard-page/AdminDashboardPage").then(
    ({ AdminDashboardPage }) => ({
      default: AdminDashboardPage,
    })
  )
);

const ThreePlaygroundPage = lazy(() =>
  import(
    "./pages/admin/playground/three-playground-page/ThreePlaygroundPage"
  ).then(({ ThreePlaygroundPage }) => ({ default: ThreePlaygroundPage }))
);

const BaseCharacterPlayground = lazy(() =>
  import("./pages/dev/base-character-playground/BaseCharacterPlayground").then(
    ({ BaseCharacterPlayground }) => ({ default: BaseCharacterPlayground })
  )
);

const TextureTool = lazy(() =>
  import("./pages/dev/texture/TextureTool").then(({ TextureTool }) => ({
    default: TextureTool,
  }))
);

const CanvasUIPage = lazy(() =>
  import("./pages/admin/playground/canvas-ui/CanvasUIPage").then(
    ({ CanvasUIPage }) => ({ default: CanvasUIPage })
  )
);

const GameLogo = lazy(() =>
  import("./components/GameLogo").then(({ GameLogo }) => ({
    default: GameLogo,
  }))
);

const TestThreeRenderPage = lazy(() =>
  import("./pages/dev/test-three-render/TestThreeRender").then(
    ({ TestThreeRender }) => ({ default: TestThreeRender })
  )
);

const MonitoringPage = lazy(() =>
  import("./pages/admin/monitoring/MonitoringPage").then(
    ({ MonitoringPage }) => ({ default: MonitoringPage })
  )
);

const StressTestPage = lazy(() =>
  import("./pages/admin/stress-test/StressTestPage").then(
    ({ StressTestPage }) => ({ default: StressTestPage })
  )
);

// Add to routes
const ROUTES = [
  {
    path: "/character",
    children: [
      {
        path: "select",
        element: (
          <AssetGuard>
            <CharacterSelectionPage />
          </AssetGuard>
        ),
      },
      {
        path: "create",
        element: (
          <AssetGuard>
            <CharacterCreationPage />
          </AssetGuard>
        ),
      },
    ],
  },
  {
    path: "/internal",
    children: [
      {
        path: "set-token",
        element: <SetTokenPage />,
      },
    ],
  },
  {
    path: "/game",
    element: (
      <AssetGuard>
        <LoadingTaskWrapper
          task={{
            id: GameLoadingTaskId.LoadingGameWorld,
            meta: {
              type: "spinner",
              text: "Loading World...",
            },
          }}
        >
          <GamePage />
        </LoadingTaskWrapper>
      </AssetGuard>
    ),
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/register",
    element: <RegisterPage />,
  },
  ...createRealmsbaseRoutes("/admin/realmsbase"),
  {
    path: "/admin",
    children: [
      {
        path: "playground",
        children: [
          {
            path: "canvas-ui",
            element: <CanvasUIPage />,
          },
          {
            path: "",
            element: (
              <AdminGuard>
                <ThreePlaygroundPage />
              </AdminGuard>
            ),
          },
        ],
      },
      {
        path: "monitoring",
        element: <MonitoringPage />,
      },
      {
        path: "stress-test",
        element: <StressTestPage />,
      },
      {
        path: "",
        element: (
          <AdminGuard>
            <AdminDashboardPage />
          </AdminGuard>
        ),
      },
    ],
  },

  // Dev pages
  ...(IS_DEV
    ? [
        {
          path: "/nine-patch-image",
          element: <NinePatchImageEditor />,
        },
        {
          path: "/base-character-playground",
          element: (
            <AssetGuard>
              <BaseCharacterPlayground />
            </AssetGuard>
          ),
        },
        {
          path: "/texture-tool",
          element: (
            <AssetGuard>
              <TextureTool />
            </AssetGuard>
          ),
        },
        {
          path: "/test-three-render",
          element: (
            <AssetGuard>
              <TestThreeRenderPage />
            </AssetGuard>
          ),
        },
        {
          path: "/logo",
          element: (
            <Box
              height={"100vh"}
              width={"100vw"}
              display={"flex"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <GameLogo sizePX={window.innerHeight / 3} />
            </Box>
          ),
        },
      ]
    : []),
  {
    path: "*",
    element: <Navigate to="/login" />,
  },
];

export const ROUTER = createBrowserRouter(ROUTES);
